import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { BarChart, Download, FileText, Calendar, Building2, TrendingUp, PieChart, LineChart, Trash2, Eye, FileType } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LayoutWrapper } from '@/components/LayoutWrapper';
import { PageHeader } from '@/components/PageHeader';
import { format } from 'date-fns';

interface Report {
  id: string;
  report_type: string;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  url: string | null;
  metadata: any;
  units: {
    name: string;
  };
  created_by_user?: {
    name: string;
  };
}

const Reports = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'excel' | 'csv'>('pdf');

  useEffect(() => {
    fetchReports();
  }, [user]);

  const fetchReports = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('reports')
        .select(`
          *,
          units!inner(name),
          created_by_user:users_public!reports_created_by_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading reports",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (reportType: string) => {
    if (!user || generating) return;

    setGenerating(true);
    try {
      // Get first unit for demo
      const { data: units } = await supabase
        .from('units')
        .select('id')
        .limit(1);

      if (!units || units.length === 0) {
        toast({
          title: "No units found",
          description: "Please create a unit first before generating reports.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Generating report...",
        description: "Please wait while we create your report.",
      });

      // Generate actual report using the edge function
      const { data, error: pdfError } = await supabase.functions.invoke('generate-pdf-export', {
        body: {
          exportType: reportType,
          format: selectedFormat,
          parameters: {
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date().toISOString(),
            includeSignatures: true,
            includeCitations: true
          }
        }
      });

      if (pdfError) throw pdfError;

      // Create database entry with the generated file URL
      const { error } = await supabase
        .from('reports')
        .insert({
          report_type: reportType,
          unit_id: units[0].id,
          created_by: user.id,
          period_start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          period_end: new Date().toISOString(),
          url: data?.downloadUrl || null,
          metadata: {
            format: selectedFormat,
            file_name: data?.fileName
          }
        });

      if (error) throw error;

      toast({
        title: "Report generated successfully!",
        description: `Your ${reportType.replace('_', ' ')} report is ready to download.`,
      });

      fetchReports();
    } catch (error: any) {
      console.error('Error generating report:', error);
      toast({
        title: "Error generating report",
        description: error.message || "Failed to generate report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = (report: Report) => {
    if (report.url) {
      window.open(report.url, '_blank');
    } else {
      toast({
        title: "File not available",
        description: "This report file is not available for download.",
        variant: "destructive",
      });
    }
  };

  const deleteReport = async (reportId: string) => {
    setDeleting(reportId);
    try {
      const { error } = await supabase
        .from('reports')
        .delete()
        .eq('id', reportId);

      if (error) throw error;

      toast({
        title: "Report deleted successfully",
        description: "The report has been removed.",
      });

      fetchReports();
    } catch (error: any) {
      toast({
        title: "Error deleting report",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const getReportIcon = (type: string) => {
    switch (type) {
      case 'compliance':
        return <BarChart className="h-5 w-5 text-blue-500" />;
      case 'incident_summary':
        return <PieChart className="h-5 w-5 text-red-500" />;
      case 'trend_analysis':
        return <LineChart className="h-5 w-5 text-green-500" />;
      case 'temperature_log':
        return <TrendingUp className="h-5 w-5 text-purple-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const getReportTypeLabel = (type: string) => {
    switch (type) {
      case 'compliance':
        return 'Compliance Report';
      case 'incident_summary':
        return 'Incident Summary';
      case 'trend_analysis':
        return 'Trend Analysis';
      case 'temperature_log':
        return 'Temperature Log';
      default:
        return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'pdf':
        return '📄';
      case 'excel':
        return '📊';
      case 'csv':
        return '📋';
      default:
        return '📄';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20">
      <PageHeader
        title="Reports & Analytics"
        description="Generate and view compliance reports"
        backTo="/dashboard"
      />

      <main className="container mx-auto px-4 py-8">
        {/* Format Selection */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <Label className="text-sm font-medium">Export Format:</Label>
            <Select value={selectedFormat} onValueChange={(value: 'pdf' | 'excel' | 'csv') => setSelectedFormat(value)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">📄 PDF</SelectItem>
                <SelectItem value="excel">📊 Excel</SelectItem>
                <SelectItem value="csv">📋 CSV</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Quick Generate Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-crystal-white mb-4">Generate New Report</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="glass-panel hover:glass-panel-elevated transition-all duration-200 cursor-pointer" 
                  onClick={() => generateReport('compliance')}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <BarChart className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Compliance Report</CardTitle>
                    <CardDescription className="text-xs">Overall compliance metrics</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Button size="sm" className="w-full cta-primary" disabled={generating}>
                  {generating ? 'Generating...' : `Generate ${getFormatIcon(selectedFormat)}`}
                </Button>
              </CardContent>
            </Card>

            <Card className="glass-panel hover:glass-panel-elevated transition-all duration-200 cursor-pointer"
                  onClick={() => generateReport('incident_summary')}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <PieChart className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Incident Summary</CardTitle>
                    <CardDescription className="text-xs">Incident analysis & trends</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Button size="sm" className="w-full cta-primary" disabled={generating}>
                  {generating ? 'Generating...' : `Generate ${getFormatIcon(selectedFormat)}`}
                </Button>
              </CardContent>
            </Card>

            <Card className="glass-panel hover:glass-panel-elevated transition-all duration-200 cursor-pointer"
                  onClick={() => generateReport('trend_analysis')}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <LineChart className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Trend Analysis</CardTitle>
                    <CardDescription className="text-xs">Performance over time</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Button size="sm" className="w-full cta-primary" disabled={generating}>
                  {generating ? 'Generating...' : `Generate ${getFormatIcon(selectedFormat)}`}
                </Button>
              </CardContent>
            </Card>

            <Card className="glass-panel hover:glass-panel-elevated transition-all duration-200 cursor-pointer"
                  onClick={() => generateReport('temperature_log')}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <TrendingUp className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Temperature Log</CardTitle>
                    <CardDescription className="text-xs">Temperature monitoring</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Button size="sm" className="w-full cta-primary" disabled={generating}>
                  {generating ? 'Generating...' : `Generate ${getFormatIcon(selectedFormat)}`}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Reports List */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-crystal-white mb-4">Recent Reports</h2>
          
          {reports.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-crystal-white mb-2">No reports yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Generate your first report to get insights into your food safety compliance and performance.
              </p>
              <Button className="cta-primary" onClick={() => generateReport('compliance')} disabled={generating}>
                <BarChart className="h-4 w-4 mr-2" />
                {generating ? 'Generating...' : 'Generate First Report'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <Card key={report.id} className="glass-panel hover:glass-panel-elevated transition-all duration-200">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-lg bg-primary/10">
                          {getReportIcon(report.report_type)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{getReportTypeLabel(report.report_type)}</CardTitle>
                          <CardDescription className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {report.units.name}
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(report.created_at), 'MMM d, yyyy')}
                            </div>
                            {report.metadata?.format && (
                              <div className="flex items-center gap-1">
                                <FileType className="h-3 w-3" />
                                {report.metadata.format.toUpperCase()}
                              </div>
                            )}
                            {report.period_start && report.period_end && (
                              <div className="text-xs">
                                Period: {format(new Date(report.period_start), 'MMM d')} - {format(new Date(report.period_end), 'MMM d')}
                              </div>
                            )}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={report.url ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"}>
                          {report.url ? 'Ready' : 'Generated'}
                        </Badge>
                        <Button size="sm" variant="outline" className="cta-secondary" onClick={() => downloadReport(report)} disabled={!report.url}>
                          <Download className="h-4 w-4 mr-2" />
                          {report.url ? 'Download' : 'Unavailable'}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-destructive hover:text-destructive"
                              disabled={deleting === report.id}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Report</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this report? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteReport(report.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {deleting === report.id ? 'Deleting...' : 'Delete'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Reports;